import React, { FC, useEffect, useCallback, useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SearchProps } from '@web/qmr/components/fail-codes-container-modal';
import { Label, Button, LoadingCircle } from '@packages/ui/shared';
import { useQmrState } from '@packages/contexts/qmrs';

interface FailCodeSearchModalProps {
    showMatchingFailCodes?: boolean;
    onHide(): void;
    handleSetContent: (props: SearchProps) => void;
    returnToHome: () => void;
    isLoading?: boolean;
}

export const FailCodeStartModal: FC<FailCodeSearchModalProps> = (props) => {
    const { t } = useTranslation();
    const { qmr } = useQmrState();

    const [askApprover, setAskApprover] = useState(false);
    const [viewMatching, setViewMatching] = useState<boolean>(Boolean(props.showMatchingFailCodes));
    const [searchFailCode, setSearchFailCode] = useState<boolean>(false);
    const [searchFailCodeByPart, setSearchFailCodeByPart] = useState<boolean>(false);
    const [lookBySection, setLookupBySection] = useState<boolean>(false);
    const [lookupByElectricalGridLocation, setLookupByElectricalGridLocation] = useState<boolean>(false);
    const [noTroubleFound, setNoTroubleFound] = useState<boolean>(false);
    const [accidentInvestigation, setAccidentInvestigation] = useState<boolean>(false);

    const resetAll = () => {
        setAskApprover(false);
        setViewMatching(false);
        setSearchFailCode(false);
        setSearchFailCodeByPart(false);
        setLookupBySection(false);
        setLookupByElectricalGridLocation(false);
        setNoTroubleFound(false);
        setAccidentInvestigation(false);
    };

    const handleSearchFailCode = () => {
        resetAll();
        setSearchFailCode(true);
    };

    const handleSearchFailCodeByPart = () => {
        resetAll();
        setSearchFailCodeByPart(true);
    };

    const handleLookupBySection = () => {
        resetAll();
        setLookupBySection(true);
    };

    const handleLookupByElectricalGridLocation = () => {
        resetAll();
        setLookupByElectricalGridLocation(true);
    };

    const handleNoTroubleFound = () => {
        resetAll();
        setNoTroubleFound(true);
    };

    const handleAccidentInvestigation = () => {
        resetAll();
        setAccidentInvestigation(true);
    };

    const handleViewMatching = () => {
        resetAll();
        setViewMatching(true);
    };

    const handleSetContent = () => {
        props.handleSetContent({
            askApprover,
            searchFailCode,
            searchFailCodeByPart,
            lookBySection,
            lookupByElectricalGridLocation,
            noTroubleFound,
            accidentInvestigation,
            viewMatching,
        });
    };

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>{t('qmr:sections:failCode.failCodeSearch')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form.Group>
                    <div className="mb-6">
                        <Label>{t('qmr:sections:failCode.selectOptions')}.</Label>
                    </div>

                    {props.isLoading ? (
                        <LoadingCircle />
                    ) : (
                        <>
                            {props.showMatchingFailCodes && (
                                <Form.Check
                                    className="mb-3"
                                    label={t('qmr:sections:failCode.viewMatchingFailCodes')}
                                    type="radio"
                                    id="viewMatchingFailCodes"
                                    name="failCodeSearch"
                                    checked={viewMatching}
                                    onChange={handleViewMatching}
                                />
                            )}
                        </>
                    )}

                    {qmr?.capabilities.requestFailCodeApproval && (
                        <Form.Check
                            className="mb-3"
                            label="Ask Fail Code Approver"
                            type="radio"
                            id="askApprover"
                            name="askApprover"
                            checked={askApprover}
                            onChange={() => {
                                resetAll();
                                setAskApprover(true);
                            }}
                        />
                    )}

                    <Form.Check
                        className="mb-3"
                        label={t('qmr:sections:failCode.searchFailCode')}
                        type="radio"
                        id="searchFailCode"
                        name="searchFailCode"
                        checked={searchFailCode}
                        onChange={handleSearchFailCode}
                    />

                    <Form.Check
                        className="mb-3"
                        label={t('qmr:sections:failCode.searchFailCodeByPart')}
                        type="radio"
                        id="searchFailCodeByPart"
                        name="searchFailCodeByPart"
                        checked={searchFailCodeByPart}
                        onChange={handleSearchFailCodeByPart}
                    />

                    <Form.Check
                        className="mb-3"
                        label={t('qmr:sections:failCode.lookupBySection')}
                        type="radio"
                        id="lookupBySection"
                        name="lookupBySection"
                        checked={lookBySection}
                        onChange={handleLookupBySection}
                    />

                    <Form.Check
                        className="mb-3"
                        label={'Lookup by electrical grid location'}
                        type="radio"
                        id="lookupByElectricalGridSection"
                        name="lookupByElectricalGridSection"
                        checked={lookupByElectricalGridLocation}
                        onChange={handleLookupByElectricalGridLocation}
                    />

                    <Form.Check
                        className="mb-3"
                        label={t('qmr:sections:failCode.noTroubleFound')}
                        type="radio"
                        id="noTroubleFound"
                        name="noTroubleFound"
                        checked={noTroubleFound}
                        onChange={handleNoTroubleFound}
                    />
                    {qmr && qmr.capabilities.useAccidentInvestigationOnQmr && (
                        <Form.Check
                            className="mb-3"
                            label={t('qmr:sections:failCode.accidentInvestigation')}
                            type="radio"
                            id="accidentInvestigation"
                            name="accidentInvestigation"
                            checked={accidentInvestigation}
                            onChange={handleAccidentInvestigation}
                        />
                    )}
                </Form.Group>
            </Modal.Body>

            <Modal.Footer>
                <Button
                    disabled={
                        !askApprover &&
                        !searchFailCode &&
                        !searchFailCodeByPart &&
                        !lookBySection &&
                        !lookupByElectricalGridLocation &&
                        !noTroubleFound &&
                        !accidentInvestigation &&
                        !viewMatching
                    }
                    onPress={handleSetContent}
                    variant="primary"
                >
                    {t('qmr:sections:failCode.continue')}
                </Button>
            </Modal.Footer>
        </>
    );
};
